import React, { useRef } from "react";
import { Warning } from "@procore/core-icons";
import {
   getDaysBetweenDetachedDays,
   getDetachedDay,
} from "@laborchart-modules/common/dist/datetime";
import { Popover, colors } from "@procore/core-react";
import { COLUMN_WIDTHS } from "../configure-panel";
import { useBoardsConfig } from "../hooks/use-boards-config";

export type TagRenderable = {
   color: string;
   abbreviation: string;
   require_expr_date?: boolean;
   expr_days_warning?: number | null;
   expr_date?: string | null;
};

type TaskCardTag = TagRenderable & {
   name: string;
   require_expr_date: boolean;
   expr_days_warning: number | null;
   expr_date: string | null;
};

function isWithinWarningRangeOrExpired(exprDate: string, warningDays: number): boolean {
   if (!exprDate || !warningDays) return false;

   const today = getDetachedDay(new Date());
   const exprDateObj = parseInt(exprDate);

   // Early return if the tag is already expired
   if (exprDateObj < today) return true;

   const diffDays = getDaysBetweenDetachedDays(today, exprDateObj);

   return diffDays <= warningDays;
}

function getWarningColor(exprDate: string, warningDays: number): string {
   if (!exprDate || !warningDays) return "";

   const today = getDetachedDay(new Date());
   const exprDateObj = parseInt(exprDate);

   if (exprDateObj > today) {
      return colors.yellow50;
   } else {
      return colors.red50;
   }
}

export function tagRenderer(tags: TagRenderable[]) {
   return tags.map((tag, index) => (
      <div
         key={index}
         className="task-card-tag"
         style={{
            backgroundColor: `${tag.color}33`,
            color: `color-mix(in srgb, ${tag.color}, black 20%)`,
         }}
      >
         {tag.abbreviation.toUpperCase()}
         {tag.require_expr_date &&
            tag.expr_date &&
            tag.expr_days_warning &&
            isWithinWarningRangeOrExpired(tag.expr_date, tag.expr_days_warning) && (
               <Warning
                  size="sm"
                  className="warning-icon"
                  data-testid="warning-icon"
                  style={{
                     color: getWarningColor(tag.expr_date, tag.expr_days_warning),
                  }}
               />
            )}
      </div>
   ));
}

export function TagDisplay({ tags }: { tags: TaskCardTag[] }) {
   const config = useBoardsConfig();
   const containerRef = useRef<HTMLDivElement>(null);

   // Sort tags alphabetically by abbreviation
   const allTags = [...tags].sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

   // Calculate how many tags can fit in the smallest width of the column without wrapping or truncating
   const calculateNumberOfTagsToShow = () => {
      // Use the width of the actual container, default to the configured column width.
      /* istanbul ignore next */
      const container = containerRef.current?.closest(".b-task-board-card");
      const containerWidth =
         container?.getBoundingClientRect().width ?? COLUMN_WIDTHS[config.columnWidth];
      if (!containerWidth) return 1;
      // Constants from CSS
      const avgTagWidth = 56; // Average tag width including padding
      const tagGap = 4;
      const remainderTagWidth = 35; // "+X" tag typical width

      // First calculate without considering remainder
      const spacePerTag = avgTagWidth + tagGap;
      const maxTagsWithoutRemainder = Math.floor(containerWidth / spacePerTag);

      // If we can show all tags, no need for remainder
      if (maxTagsWithoutRemainder >= allTags.length) {
         return allTags.length;
      }

      // Otherwise, account for remainder indicator
      const availableWidthWithRemainder = containerWidth - remainderTagWidth - tagGap;
      const maxTagsWithRemainder = Math.floor(availableWidthWithRemainder / spacePerTag);

      // Always show at least one tag
      const result = Math.max(1, maxTagsWithRemainder);

      return result;
   };

   const numberOfTagsToShow = calculateNumberOfTagsToShow();
   const displayTags = config.showAllTags ? allTags : allTags.slice(0, numberOfTagsToShow);
   const remainingCount = Math.max(0, allTags.length - numberOfTagsToShow);

   const tagPopover = (tags: TaskCardTag[]) => {
      return (
         <Popover.Content placement="top" className="task-card-tag-popover">
            <div className="task-card-tag-popover-container" data-testid="tag-popover-container">
               {tagRenderer(tags)}
            </div>
         </Popover.Content>
      );
   };

   return (
      <div className="task-card-tag-container" ref={containerRef}>
         {tagRenderer(displayTags)}
         {!config.showAllTags && remainingCount > 0 && (
            <Popover trigger="click" placement="top" overlay={tagPopover(allTags)}>
               <div className="task-card-tag count-tag" onClick={(e) => e.stopPropagation()}>
                  +{remainingCount}
               </div>
            </Popover>
         )}
      </div>
   );
}
